import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Search, Edit2, Trash2, User, Phone, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantsPage = () => {
  const { t } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    id_passport: '',
    address: '',
    emergency_contact: '',
    notes: '',
    company_id: ''
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants`, { withCredentials: true });
      setTenants(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await axios.put(`${API_URL}/api/tenants/${editingTenant.id}`, formData, { withCredentials: true });
      } else {
        // For new tenants, company_id will be set by backend from user context
        await axios.post(`${API_URL}/api/tenants`, formData, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name || '',
      phone: tenant.phone || '',
      email: tenant.email || '',
      id_passport: tenant.id_passport || '',
      address: tenant.address || '',
      emergency_contact: tenant.emergency_contact || '',
      notes: tenant.notes || '',
      company_id: tenant.company_id || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchTenants();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setEditingTenant(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      id_passport: '',
      address: '',
      emergency_contact: '',
      notes: '',
      company_id: ''
    });
  };

  const filteredTenants = tenants.filter(tenant => 
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.phone.includes(searchTerm) ||
    (tenant.email && tenant.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('tenants')}</h1>
          <p className="text-gray-500 text-sm mt-1">{tenants.length} {t('total')}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-tenant-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addTenant')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder={t('search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-none"
          data-testid="tenants-search-input"
        />
      </div>

      {/* Tenants Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('tenantName')}</th>
              <th>{t('phone')}</th>
              <th>{t('email')}</th>
              <th>{t('idPassport')}</th>
              <th>{t('emergencyContact')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.length > 0 ? (
              filteredTenants.map((tenant) => (
                <tr key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {tenant.name}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-gray-400" />
                      {tenant.phone}
                    </div>
                  </td>
                  <td>
                    {tenant.email ? (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-gray-400" />
                        {tenant.email}
                      </div>
                    ) : '-'}
                  </td>
                  <td>{tenant.id_passport || '-'}</td>
                  <td>{tenant.emergency_contact || '-'}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEdit(tenant)} 
                        className="p-1 hover:bg-gray-100"
                        data-testid={`edit-tenant-${tenant.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(tenant.id)} 
                        className="p-1 hover:bg-red-100 text-red-600"
                        data-testid={`delete-tenant-${tenant.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingTenant ? t('editTenant') : t('addTenant')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('tenantName')} *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                  data-testid="tenant-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('phone')} *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                  data-testid="tenant-phone-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('email')}</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('idPassport')}</label>
                <Input
                  value={formData.id_passport}
                  onChange={(e) => setFormData(prev => ({ ...prev, id_passport: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('address')}</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="rounded-none mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('emergencyContact')}</label>
              <Input
                value={formData.emergency_contact}
                onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact: e.target.value }))}
                className="rounded-none mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="3"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="tenant-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantsPage;
